import { useEffect, useState } from 'react';
import { collection, query, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { normaliseMarket } from '../lib/markets';
import { toDateSafe } from '../lib/dateUtils';

export interface JobRecord {
  id: string;
  templateId: string;
  templateVersion?: string;
  /** Human-readable label stored on the job document at creation time. */
  name?: string;
  remotionComponentId?: string;
  market?: string;
  /**
   * Camel-case field names match the existing jobs Firestore collection convention
   * (templateId, templateVersion, remotionComponentId, …).
   * Template documents use snake_case to mirror the spec schema (§4).
   */
  deliverableId?: string;
  format?: string;
  outputFormat?: string;
  language?: string;
  status: 'queued' | 'preprocessing' | 'rendering' | 'uploading' | 'completed' | 'failed' | 'pending_assets' | 'draft';
  progress: number;
  createdAt: Date;
  completedAt?: Date;
  outputVideoUrl?: string;
  previewVideoUrl?: string;
  outputPath?: string;
  error?: string | { message: string; code?: string; stage?: string };
  attempt?: number;
  maxAttempts?: number;
  retryCount?: number;
  lastError?: string;
  retriedAt?: Date;
  /** Whether the job was triggered from the UI or via an external pipeline. Defaults to 'manual'. */
  jobSource?: 'manual' | 'pipeline';
  /** CI/CD run identifier for pipeline jobs. */
  runId?: string | null;
  /** Pipeline path variant ("path_1" | "path_2") controlling output root. */
  pipelinePath?: string | null;
  /** Slot IDs that must be filled by the operator before rendering can proceed. */
  missing_required_slots?: string[];
  /** Slot IDs that had no matching manifest entry and also need operator attention. */
  unmatched_slots?: string[];
  /** Structured render props keyed by slot_id. Populated from pipeline handoff. */
  props?: Record<string, unknown>;
  /** Legacy render payload shape keyed by slot_id. */
  inputProps?: Record<string, unknown>;
  /** Legacy asset mapping payload kept for backward compatibility. */
  assetMappings?: unknown;
}

/**
 * Fetches all jobs from Firestore ordered by createdAt DESC.
 * Market filtering is applied client-side (same pattern as useRuns).
 */
export function useJobs(market: string) {
  const [jobs, setJobs] = useState<JobRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);

    const q = query(collection(db, 'jobs'), orderBy('createdAt', 'desc'));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const all: JobRecord[] = snapshot.docs.flatMap((doc) => {
          const d = doc.data();
          const status = d.status ?? 'queued';
          if (
            status === 'draft' ||
            status === 'discarded' ||
            status === 'consumed' ||
            status === 'submitted'
          ) {
            return [];
          }

          return [{
            id: doc.id,
            templateId: d.templateId ?? '',
			name: d.name ?? undefined,
            templateVersion: d.templateVersion ?? undefined,
            remotionComponentId: d.remotionComponentId ?? undefined,
            market: normaliseMarket(d.market ?? 'global'),
            deliverableId: d.deliverableId ?? undefined,
            format: d.format ?? undefined,
            outputFormat: d.outputFormat ?? undefined,
            language: d.language ?? undefined,
            status,
            progress: d.progress ?? 0,
            createdAt: toDateSafe(d.createdAt),
            completedAt: d.completedAt != null ? toDateSafe(d.completedAt) : undefined,
            outputVideoUrl: d.outputVideoUrl ?? undefined,
            previewVideoUrl: d.previewVideoUrl ?? undefined,
            outputPath: d.outputPath ?? undefined,
            error: d.error ?? undefined,
            attempt: typeof d.attempt === 'number' ? d.attempt : undefined,
            maxAttempts: typeof d.maxAttempts === 'number' ? d.maxAttempts : undefined,
            retryCount: typeof d.retryCount === 'number' ? d.retryCount : undefined,
            lastError: typeof d.lastError === 'string' ? d.lastError : undefined,
            retriedAt: d.retriedAt != null ? toDateSafe(d.retriedAt) : undefined,
            jobSource: d.jobSource === 'pipeline' ? 'pipeline' : 'manual',
            runId: d.runId ?? null,
            pipelinePath: d.pipelinePath ?? null,
            missing_required_slots: Array.isArray(d.missing_required_slots) ? d.missing_required_slots : undefined,
            unmatched_slots: Array.isArray(d.unmatched_slots) ? d.unmatched_slots : undefined,
            props: typeof d.props === 'object' && d.props !== null ? d.props as Record<string, unknown> : undefined,
            inputProps:
              typeof d.inputProps === 'object' && d.inputProps !== null
                ? d.inputProps as Record<string, unknown>
                : undefined,
            assetMappings: d.assetMappings ?? undefined,
          }];
        });

        // Client-side market filter (market === 'all' shows everything)
        const filtered =
          market === 'all' ? all : all.filter((j) => j.market === market);

        setJobs(filtered);
        setLoading(false);
      },
      (err) => {
        setError(err.message);
        setLoading(false);
      },
    );

    return () => unsubscribe();
  }, [market]);

  return { jobs, loading, error };
}
